/**
 * TBI14 Concussion Summary Component
 * Accepts pasted clinical notes and/or uploaded PDF/DOCX files,
 * sends to backend for AI-generated concussion summary.
 * Summary is editable — edits are sent as feedback to improve future summaries.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_URL || 'https://clinhx-backend-v2.onrender.com/api';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const backButtonStyle = { display: 'flex', alignItems: 'center', color: '#4b5563', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', marginBottom: '8px', fontSize: '14px', padding: '0' };

const BackButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={backButtonStyle}
    onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
    onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
    Back to Dashboard
  </button>
);

/**
 * Fire-and-forget: submit feedback if summary was edited.
 */
const submitFeedback = (originalSummary, editedSummary, inputSnippet) => {
  if (!originalSummary || !editedSummary || originalSummary === editedSummary) return;

  fetch(`${API_URL}/tbi14/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({
      originalSummary,
      editedSummary,
      inputSnippet: inputSnippet ? inputSnippet.slice(0, 500) : null
    })
  }).catch(() => {
    // Non-blocking — silently ignore errors
  });
};

const TBI14Summary = () => {
  const navigate = useNavigate();
  const [pastedText, setPastedText] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const editableRef = useRef(null);
  const originalSummaryRef = useRef('');
  const inputSnippetRef = useRef('');

  // Send feedback before navigating away (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (summary && editableRef.current) {
        const currentText = editableRef.current.innerText;
        if (currentText !== originalSummaryRef.current) {
          submitFeedback(originalSummaryRef.current, currentText, inputSnippetRef.current);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [summary]);

  const handleFilesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
      f.type === 'application/pdf' ||
      f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      f.name.endsWith('.pdf') || f.name.endsWith('.docx')
    );
    setFiles(prev => [...prev, ...droppedFiles]);
  }, []);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async () => {
    if (!pastedText.trim() && files.length === 0) {
      setError('Please paste clinical notes or upload at least one file.');
      return;
    }

    setLoading(true);
    setError(null);
    setSummary('');
    setIsEdited(false);

    const formData = new FormData();
    if (pastedText.trim()) {
      formData.append('pastedText', pastedText.trim());
    }
    files.forEach(file => formData.append('files', file));

    // Save input snippet for feedback context
    inputSnippetRef.current = pastedText.trim().slice(0, 500);

    try {
      const response = await fetch(`${API_URL}/tbi14/summarise`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate summary');
      }

      setSummary(data.summary);
      originalSummaryRef.current = data.summary;
    } catch (err) {
      setError(err.message || 'An error occurred while generating the summary.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    const textToCopy = editableRef.current ? editableRef.current.innerText : summary;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sendFeedbackAndReset = () => {
    if (editableRef.current) {
      const currentText = editableRef.current.innerText;
      submitFeedback(originalSummaryRef.current, currentText, inputSnippetRef.current);
    }
  };

  const handleReset = () => {
    sendFeedbackAndReset();
    setPastedText('');
    setFiles([]);
    setSummary('');
    setError(null);
    setCopied(false);
    setIsEdited(false);
  };

  const handleBackToDashboard = () => {
    sendFeedbackAndReset();
    navigate('/dashboard');
  };

  const handleInput = () => {
    if (editableRef.current) {
      const currentText = editableRef.current.innerText;
      setIsEdited(currentText !== originalSummaryRef.current);
    }
  };

  const formatSummaryHTML = (text) => {
    const raw = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>')
      .replace(/^/, '<p>')
      .replace(/$/, '</p>');
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i'],
      ALLOWED_ATTR: []
    });
  };

  // --- RESULT VIEW ---
  if (summary) {
    return (
      <div style={styles.container}>
        <BackButton onClick={handleBackToDashboard} />
        <div style={styles.header}>
          <h1 style={styles.title}>TBI14 Concussion Summary</h1>
          <p style={styles.subtitle}>AI-generated clinical summary</p>
        </div>

        <div style={styles.resultCard}>
          <div style={styles.resultHeader}>
            <div>
              <h3 style={styles.resultTitle}>Summary</h3>
              <p style={styles.editHint}>
                {isEdited ? 'Edited — your changes will be used to improve future summaries' : 'Click text below to edit before copying'}
              </p>
            </div>
            <div style={styles.resultActions}>
              <button onClick={handleCopy} style={styles.copyButton}>
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
              <button onClick={handleReset} style={styles.newButton}>
                New Summary
              </button>
            </div>
          </div>
          <div
            ref={editableRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            style={{
              ...styles.summaryText,
              ...styles.editableArea,
              ...(isEdited ? styles.editedBorder : {})
            }}
            dangerouslySetInnerHTML={{ __html: formatSummaryHTML(summary) }}
          />
        </div>
      </div>
    );
  }

  // --- LOADING VIEW ---
  if (loading) {
    return (
      <div style={styles.container}>
        <BackButton onClick={() => navigate('/dashboard')} />
        <div style={styles.header}>
          <h1 style={styles.title}>TBI14 Concussion Summary</h1>
        </div>
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Generating summary... This may take 30–60 seconds</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // --- INPUT FORM ---
  return (
    <div style={styles.container}>
      <BackButton onClick={() => navigate('/dashboard')} />
      <div style={styles.header}>
        <h1 style={styles.title}>TBI14 Concussion Summary</h1>
        <p style={styles.subtitle}>Paste clinical notes and/or upload documents for AI-generated concussion summary</p>
      </div>

      <div style={styles.formArea}>
        {/* Pasted text */}
        <div>
          <label style={styles.label}>Clinical Notes</label>
          <textarea
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Paste clinical notes here..."
            style={styles.textarea}
            rows={12}
          />
        </div>

        {/* File drop zone */}
        <div
          style={styles.dropZone}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <span style={styles.dropIcon}>📄</span>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#374151' }}>Upload Documents (Optional)</h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Drag & drop or click — PDF, DOCX (max 5 files)</p>
          <input
            type="file"
            accept=".pdf,.docx"
            multiple
            onChange={handleFilesChange}
            style={styles.fileInput}
          />
        </div>

        {/* Selected files list */}
        {files.length > 0 && (
          <div style={styles.fileList}>
            <div style={styles.fileListHeader}>
              <h4 style={styles.fileListTitle}>Selected Files ({files.length})</h4>
              <button onClick={() => setFiles([])} style={styles.clearAllButton}>Clear All</button>
            </div>
            {files.map((file, index) => (
              <div key={index} style={styles.fileItem}>
                <span>📄 {file.name} ({formatFileSize(file.size)})</span>
                <button onClick={() => removeFile(index)} style={styles.removeButton}>✕</button>
              </div>
            ))}
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <button
          onClick={handleSubmit}
          disabled={!pastedText.trim() && files.length === 0}
          style={{
            ...styles.submitButton,
            opacity: (!pastedText.trim() && files.length === 0) ? 0.6 : 1
          }}
        >
          Generate Concussion Summary
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '900px', margin: '0 auto', padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { textAlign: 'center', marginBottom: '32px' },
  title: { fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' },
  subtitle: { fontSize: '16px', color: '#666', margin: 0 },

  formArea: { display: 'flex', flexDirection: 'column', gap: '20px' },
  label: { display: 'block', fontWeight: '600', color: '#333', marginBottom: '6px', fontSize: '14px' },
  textarea: {
    width: '100%', padding: '12px 16px', border: '2px solid #ddd', borderRadius: '8px',
    fontSize: '14px', lineHeight: '1.6', resize: 'vertical', fontFamily: 'inherit',
    boxSizing: 'border-box'
  },

  dropZone: {
    border: '2px dashed #ccc', borderRadius: '12px', padding: '32px',
    textAlign: 'center', cursor: 'pointer', backgroundColor: '#fafafa', position: 'relative'
  },
  dropIcon: { fontSize: '48px', display: 'block', marginBottom: '12px' },
  fileInput: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' },

  fileList: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' },
  fileListHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  fileListTitle: { margin: '0', fontSize: '14px', fontWeight: '600', color: '#374151' },
  clearAllButton: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', fontWeight: '500', padding: '4px 8px' },
  fileItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#fff', borderRadius: '6px', marginBottom: '8px', fontSize: '14px' },
  removeButton: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: '4px 8px' },

  error: { backgroundColor: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', border: '1px solid #fecaca' },

  submitButton: {
    padding: '16px 32px', backgroundColor: '#0d9488', color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer'
  },

  // Loading
  loadingCard: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '48px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center'
  },
  spinner: {
    width: '48px', height: '48px', border: '4px solid #e5e7eb', borderTop: '4px solid #0d9488',
    borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px'
  },
  loadingText: { fontSize: '16px', color: '#6b7280' },

  // Result
  resultCard: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  resultHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '16px', flexWrap: 'wrap', gap: '12px'
  },
  resultTitle: { fontSize: '20px', fontWeight: '600', margin: '0 0 4px 0' },
  resultActions: { display: 'flex', gap: '8px' },
  copyButton: {
    padding: '8px 16px', backgroundColor: '#0d9488', color: '#fff', border: 'none',
    borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },
  newButton: {
    padding: '8px 16px', backgroundColor: '#fff', color: '#0d9488', border: '2px solid #0d9488',
    borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },
  summaryText: {
    fontSize: '15px', lineHeight: '1.8', color: '#1f2937', whiteSpace: 'pre-wrap'
  },
  editableArea: {
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    outline: 'none',
    cursor: 'text',
    transition: 'border-color 0.2s'
  },
  editedBorder: {
    borderColor: '#0d9488'
  },
  editHint: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
    fontStyle: 'italic'
  }
};

export default TBI14Summary;
